import { ReactElement, useContext, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { Button, Loading } from "@wfp/ui"

import { DataUploadContext } from "../../providers/DataUploadContext"
import useCommon from "../../hooks/useCommon"

import ProgressStepper from "../../components/Shared/ProgressStepper/ProgressStepper"

import { steps } from "../../constants/markets"
import { ReportStatuses } from "../../constants/prices"
import { DataUploadSteps, IStepData, ISubStep } from "../../interfaces/markets"
import { useChangeReportStatusMutation } from "../../services/marketprices/reports"

const DataUploadPage = (): ReactElement => {
  const { rId } = useParams<{ rId: string }>()

  const navigate = useNavigate()
  const { showSnackBar } = useCommon()
  const {
    filters,
    completedStep,
    setCompletedStep,
    showNewMapping,
    setShowNewMapping,
    handleNewMapping,
    newMapping,
    handleNextMapping,
    isLoading,
    disabledNext,
    setDisabledNext,
  } = useContext(DataUploadContext)
  const prevFiltersRef = useRef(filters)

  const [activeStep, setActiveStep] = useState<number>(rId ? DataUploadSteps.VALIDATION : DataUploadSteps.DATA)
  const [activeSubStep, setActiveSubStep] = useState<number>(1)

  const [changeReportStatus, { isLoading: isLoadingStatus }] = useChangeReportStatusMutation()

  const stepData: IStepData = {
    filters,
    showNewMapping,
  }

  const getVisibleSubSteps = (stepId: number): ISubStep[] => {
    const step = steps.find(item => item.id === stepId)
    if (!step?.substeps) return []

    return step.substeps.filter(substep => {
      if (!substep.isVisible) return true
      return substep.isVisible(stepData)
    })
  }

  const visibleSubSteps = getVisibleSubSteps(activeStep)
  const currentStep = steps.find(item => item.id === activeStep)
  const hasSubSteps = visibleSubSteps.length > 0

  const validateStep = (): boolean => {
    switch (activeStep) {
      case DataUploadSteps.DATA:
        return (!!filters.file || !!filters.reportId) && !!filters.adm0Code
      case DataUploadSteps.MAPPING:
        if (hasSubSteps && activeSubStep === visibleSubSteps.length) return !!newMapping?.isCompleted
        return !!filters.mappingSchemaId || !!filters.mappingSchemaName
      default:
        return true
    }
  }

  const handleBack = (): void => {
    setDisabledNext(false)
    if (activeStep === DataUploadSteps.MAPPING && showNewMapping && activeSubStep === 1) {
      return setShowNewMapping(false)
    }
    if (hasSubSteps && activeSubStep > 1) {
      setActiveSubStep(prev => prev - 1)
      return
    }

    if (activeStep > DataUploadSteps.DATA) {
      const prevStepId = activeStep - 1
      setActiveStep(prevStepId)

      const prevVisibleSubsteps = getVisibleSubSteps(prevStepId)
      setActiveSubStep(prevVisibleSubsteps.length || 1)
    } else {
      navigate(-1)
    }
  }

  const handleNext = async (): Promise<void> => {
    if (activeStep === DataUploadSteps.MAPPING && filters.mappingSchemaName && !showNewMapping) {
      if (filters.mappingSchemaName !== prevFiltersRef.current.mappingSchemaName) {
        await handleNewMapping()
        setCompletedStep(activeStep - 1)
        prevFiltersRef.current = { ...filters }
      }
      return setShowNewMapping(true)
    }

    if (hasSubSteps && activeSubStep < visibleSubSteps.length) {
      setActiveSubStep(prev => prev + 1)
      return
    }

    if (activeStep <= steps.length) {
      if (activeStep > completedStep || JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
        try {
          switch (activeStep) {
            case DataUploadSteps.MAPPING:
              await handleNextMapping()
              break
            case DataUploadSteps.FINALIZE:
              await changeReportStatus({ id: filters.reportId!, status: ReportStatuses.Pending }).unwrap()
              showSnackBar("Report submitted successfully")
              break
            default:
              break
          }
        } catch (error) {
          const err = error as { data?: string; message?: string }
          if (err?.message) return showSnackBar(err?.message, "error")
          await showSnackBar("An error occurred while processing your request", "error")
          return
        }

        setCompletedStep(activeStep)
        prevFiltersRef.current = { ...filters }
      }

      if (activeStep < steps.length) {
        setActiveStep(prev => prev + 1)
        setActiveSubStep(1)
      } else {
        navigate("/market-prices/reports")
      }
    }
  }

  const getSubStepColor = (index: number): string => {
    if (index < activeSubStep) return "bg-teal-500"
    if (index === activeSubStep) return "bg-[var(--interactive-01)]"
    return "bg-gray-300"
  }

  const renderContent = (): ReactElement | null => {
    const content = hasSubSteps ? visibleSubSteps[activeSubStep - 1]?.content : currentStep?.content

    if (hasSubSteps && content && currentStep?.wrapper) {
      const WrapperComponent = currentStep.wrapper
      return <WrapperComponent mappingSchemaId={newMapping?.id ?? 0}>{content}</WrapperComponent>
    }

    return content ?? null
  }

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 125px)" }}>
      <div className="w-full border-b-2 border-solid border-gray-200 pb-1 pt-2 bg-white z-30 flex-shrink-0">
        <ProgressStepper steps={steps} currentStep={activeStep} />
      </div>

      <div className="flex-1 overflow-y-auto pt-8 px-6">
        {isLoading ? (
          <div className="min-h-[400px] w-full flex justify-center items-center">
            <Loading small withOverlay={false} />
          </div>
        ) : (
          renderContent()
        )}
      </div>

      <div className="flex justify-between w-full border-t-2 border-solid border-gray-200 pt-4 pb-4 bg-white px-6 flex-shrink-0">
        <Button kind="secondary" onClick={handleBack}>
          {activeStep > DataUploadSteps.DATA || activeSubStep > 1 ? "Back" : "Cancel"}
        </Button>

        {hasSubSteps && (
          <div className="flex justify-center gap-2">
            {visibleSubSteps.map((substep, index) => (
              <div
                key={substep.id}
                className={`h-2 w-6 rounded-full mt-2 transition-colors ${getSubStepColor(index + 1)}`}
                title={substep.label}
              />
            ))}
          </div>
        )}

        <Button onClick={handleNext} disabled={disabledNext || isLoading || !validateStep() || isLoadingStatus}>
          {activeStep < DataUploadSteps.FINALIZE || (hasSubSteps && activeSubStep < visibleSubSteps.length)
            ? steps[activeStep - 1]?.nextAction || "Next"
            : "Save"}
        </Button>
      </div>
    </div>
  )
}

export default DataUploadPage
